import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppLayout } from '../../app/appLayout.js';
import { uploadMappingApi } from '../../../api/uploadMappingApi.js';
import { transactionsApi } from '../../../api/transactionsApi.js';
import { Modal } from '../../shared/index.js';

const BANK = {
  SCHWAB: 'SCHWAB',
  CHASE: 'CHASE',
};

const UploadMappings = () => {
  const [source, setSource] = useState(BANK.SCHWAB);

  const [mappings, setMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    searchStr: '',
    vendor: '',
    description: '',
    categoryID: '',
  });

  // -----------------------
  // Helpers
  // -----------------------
  const normalizeCategories = (res) => {
    // you said: returns id, category, sub category
    // your other endpoints often respond { msg, data: { rows: [...] } }
    const rows = res?.data?.rows ?? res?.rows ?? res ?? [];
    return Array.isArray(rows) ? rows : [];
  };

  const normalizeMappings = (res) => {
    // you said: API returns { rows: [ { searchStr, vendor, description, categoryID } ] }
    const rows = res?.data?.rows ?? res?.rows ?? res ?? [];
    return Array.isArray(rows) ? rows : [];
  };

  const categoryLabel = (c) => {
    const sub = c.subCategory ?? c.sub_category ?? c.subcategory ?? c.subCategoryName ?? c.subCategoryLabel ?? c.sub_category_name;
    // if your API returns "sub category" literally, JS would likely convert to subCategory in your backend,
    // but we can’t access "sub category" as a property unless it is bracketed:
    const sub2 = c['sub category'];
    const subFinal = sub ?? sub2 ?? '';
    return `${c.category}${subFinal ? ` - ${subFinal}` : ''}`;
  };

  const categoryLabelById = useMemo(() => {
    const map = new Map();
    for (const c of categories) {
      map.set(String(c.id), categoryLabel(c));
    }
    return map;
  }, [categories]);

  // Pagination
  const totalItems = mappings.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const pagedMappings = mappings.slice(startIndex, endIndex);

  // -----------------------
  // Data fetching
  // -----------------------
  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const res = await transactionsApi.getMergedCategories();
      if (res?.msg === 'Forbidden') {
        window.location.href = '/login';
        return;
      }
      const cats = normalizeCategories(res);
      setCategories(cats);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const fetchMappings = useCallback(async (nextSource) => {
    setLoadingMappings(true);
    try {
      if (nextSource === BANK.SCHWAB) {
        const res = await uploadMappingApi.getSchwab();
        if (res?.msg === 'Forbidden') {
          window.location.href = '/login';
          return;
        }
        const rows = normalizeMappings(res);
        setMappings(rows);
        setCurrentPage(1);
      } else if (nextSource === BANK.CHASE) {
        const res = await uploadMappingApi.getChase();
        if (res?.msg === 'Forbidden') {
          window.location.href = '/login';
          return;
        }
        const rows = normalizeMappings(res);
        setMappings(rows);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Failed to fetch mappings:', err);
      setMappings([]);
    } finally {
      setLoadingMappings(false);
    }
  }, []);

  // Default to Schwab on page load
  useEffect(() => {
    fetchMappings(BANK.SCHWAB);
    fetchCategories();
  }, [fetchMappings, fetchCategories]);

  const handleSourceChange = async (e) => {
    const next = e.target.value;
    setSource(next);
    fetchMappings(next);
  };

  // -----------------------
  // Modal / form
  // -----------------------
  const resetForm = () => {
    setFormData({
      searchStr: '',
      vendor: '',
      description: '',
      categoryID: '',
    });
  };

  const openCreate = () => {
    resetForm();
    setIsModalOpen(true);

    // Ensure categories are loaded for the dropdown
    if (!loadingCategories && (!categories || categories.length === 0)) {
      fetchCategories();
    }
  };

  const closeCreate = () => {
    if (saving) return;
    setIsModalOpen(false);
    resetForm();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!formData.searchStr.trim()) return 'Search string is required.';
    if (!formData.vendor.trim()) return 'Vendor is required.';
    if (!formData.description.trim()) return 'Description is required.';
    if (!formData.categoryID) return 'Category is required.';
    return '';
  };

  const handleRemove = async (id) => {
    if (!id || !window.confirm('Remove this mapping?')) return;
    try {
      const res = await uploadMappingApi.removeMapping(id);
      if (res?.msg === 'Forbidden') {
        window.location.href = '/login';
        return;
      }
      if (res?.msg === 'error' || res?.success === false) {
        alert(res?.msg ?? res?.message ?? 'Failed to remove mapping.');
        return;
      }
      await fetchMappings(source);
    } catch (err) {
      console.error('Failed to remove mapping:', err);
      alert('Failed to remove mapping.');
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    const payload = {
      searchStr: formData.searchStr.trim(),
      vendor: formData.vendor.trim(),
      description: formData.description.trim(),
      categoryID: formData.categoryID,
    };

    setSaving(true);
    try {
      if (source === BANK.SCHWAB) {
        const res = await uploadMappingApi.createSchwab(payload);
        if (res?.msg === 'Forbidden') {
          window.location.href = '/login';
          return;
        }
        await fetchMappings(BANK.SCHWAB);
      } else if (source === BANK.CHASE) {
        const res = await uploadMappingApi.createChase(payload);
        if (res?.msg === 'Forbidden') {
          window.location.href = '/login';
          return;
        }
        await fetchMappings(BANK.CHASE);
      }

      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create mapping:', error);
      alert('Failed to create mapping.');
    } finally {
      setSaving(false);
    }
  };

  // -----------------------
  // Render
  // -----------------------
  return (
    <AppLayout>
      <div className="transactions-container">
        <div className="transactions-header">
          <h1>Upload Mappings</h1>
        </div>

        <div className="action-buttons" style={{ alignItems: 'center' }}>
          <label className="page-size-label" style={{ marginRight: 12 }}>
            Source
            <select
              className="page-size-select"
              value={source}
              onChange={handleSourceChange}
              style={{ marginLeft: 8 }}
            >
              <option value={BANK.SCHWAB}>SCHWAB</option>
              <option value={BANK.CHASE}>CHASE</option>
            </select>
          </label>

          <button className="btn btn-primary" onClick={openCreate} type="button">
            Create
          </button>
        </div>

        <div className="pagination-bar">
          <div className="pagination-left">
            <span className="pagination-summary">
              {loadingMappings ? (
                'Loading...'
              ) : (
                <>Showing {totalItems === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems}</>
              )}
            </span>
          </div>

          <div className="pagination-right">
            <label className="page-size-label">
              Page size
              <select
                className="page-size-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <button
              className="btn btn-secondary pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              type="button"
            >
              ⏮ First
            </button>

            <button
              className="btn btn-secondary pagination-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              type="button"
            >
              ◀ Prev
            </button>

            <span className="pagination-page">
              Page {currentPage} / {totalPages}
            </span>

            <button
              className="btn btn-secondary pagination-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              type="button"
            >
              Next ▶
            </button>

            <button
              className="btn btn-secondary pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              type="button"
            >
              Last ⏭
            </button>
          </div>
        </div>

        <div className="transactions-table-container">
          {loadingMappings ? (
            <div className="empty-state">
              <p>Loading mappings…</p>
            </div>
          ) : mappings.length === 0 ? (
            <div className="empty-state">
              <p>No mappings found for {source}.</p>
            </div>
          ) : (
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Search String</th>
                  <th>Vendor</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Sub Category</th>
                  <th>Remove</th>
                </tr>
              </thead>
              <tbody>
                {pagedMappings.map((m, idx) => {
                  return (
                    <tr key={m.id ?? `${m.searchStr || ''}-${m.vendor || ''}-${idx}`}>
                      <td>{m.id}</td>
                      <td>{m.searchStr}</td>
                      <td>{m.vendor}</td>
                      <td>{m.description}</td>
                      <td>{m.category}</td>
                      <td>{m.subCategory}</td>
                      <td>
                        {(source === BANK.SCHWAB || source === BANK.CHASE) && m.id != null ? (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => handleRemove(m.id)}
                            title="Remove mapping"
                          >
                            Remove
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {isModalOpen && (
          <Modal title="Create Mapping" onClose={closeCreate}>
            <form className="modal-form" onSubmit={handleCreateSubmit}>
              <div className="modal-form-group">
                <label htmlFor="searchStr">
                  Search string<span className="required-asterisk">*</span>
                </label>
                <input
                  id="searchStr"
                  name="searchStr"
                  type="text"
                  value={formData.searchStr}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="modal-form-group">
                <label htmlFor="vendor">
                  Vendor<span className="required-asterisk">*</span>
                </label>
                <input
                  id="vendor"
                  name="vendor"
                  type="text"
                  value={formData.vendor}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="modal-form-group">
                <label htmlFor="description">
                  Description<span className="required-asterisk">*</span>
                </label>
                <input
                  id="description"
                  name="description"
                  type="text"
                  value={formData.description}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="modal-form-group">
                <label htmlFor="categoryID">
                  Category<span className="required-asterisk">*</span>
                </label>
                <select
                  id="categoryID"
                  name="categoryID"
                  value={formData.categoryID}
                  onChange={handleFormChange}
                  required
                  disabled={loadingCategories}
                >
                  <option value="">
                    {loadingCategories ? 'Loading…' : 'Select category'}
                  </option>
                  {categories.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {categoryLabel(c)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeCreate} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Create'}
                </button>
              </div>

            </form>
          </Modal>
        )}
      </div>
    </AppLayout>
  );
};

export { UploadMappings };
